import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import CustomDataTable from "src/components/CustomDataTable";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getIncomingRequest, getOutgoingRequest } from "src/api/inventory";
import CustomModal from "src/components/CustomModal";
import { Button } from "src/components/ui/button";
import RequestProducts from "src/components/modals/RequestProducts";
import CustomSelect from "src/components/CustomSelect";
import { useDashboardContext } from "src/context/DashboardContext";
import { format } from "date-fns";
import { cva } from "class-variance-authority";
import CustomDropdown from "src/components/CustomDropdown";
import { EllipsisIcon } from "src/svg";
import CustomSheet from "src/components/CustomSheet";
import RequestDetail from "src/components/modals/RequestDetail";
import ApproveRequest from "src/components/modals/ApproveRequest";
import "src/array";
import useHasPermissions from "src/hooks/useHasPermissions";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import { useAuth } from "src/providers/AuthProvider";
import PermissionWrapper from "src/components/PermissionWrapper";
import { getCustomizationValue } from "src/lib/utils";

const InventoryRequest = () => {
	const { user } = useAuth();
	const { entities } = useDashboardContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const entity = searchParams.get("entity") ?? user?.store_or_warehouse_uuid;

	const options = entities?.map(({ uuid, name }) => ({ label: name, value: uuid }));

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		if (value === "0") {
			newSearchParams.delete(key);
		} else {
			newSearchParams.set(key, value);
		}

		setSearchParams(newSearchParams);
	};

	return (
		<div className="pageWrapper">
			<div className="pageTop flex-cont flex-space-between flex-align-center">
				<h2>{getCustomizationValue(user, "inventory_label", true)} Requests</h2>

				<div className="tw-flex tw-gap-3 tw-items-center">
					<PermissionWrapper permission="view_all_store_requests">
						<CustomSelect
							className="!tw-min-w-[200px]"
							placeholder="Select store/warehouse"
							value={entity}
							onChange={(value) => updateSearchParams("entity", value)}
							options={[{ value: "0", label: "All" }, ...options]}
						/>
					</PermissionWrapper>
					<CustomModal size="sm" title="Request Products" trigger={<Button className="!tw-py-2.5">Request Products</Button>}>
						<RequestProducts />
					</CustomModal>
				</div>
			</div>

			<div className="innerContent">
				<Tabs defaultValue="incoming">
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger value="incoming">Incoming Requests</TabsTrigger>
						<TabsTrigger value="outgoing">Outgoing Requests</TabsTrigger>
					</TabsList>
					<TabsContent value="incoming" className="tw-pt-10">
						<Table />
					</TabsContent>
					<TabsContent value="outgoing" className="tw-pt-10">
						<Table type="outgoing" />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};

const statusVariants = cva("tw-capitalize tw-font-semibold", {
	variants: {
		status: {
			pending: "tw-text-orange",
			approved: "tw-text-green",
		},
	},
	defaultVariants: {
		status: "default",
	},
});

const columns = (type, hasApproveInventoryPermission) => [
	{ name: "S/N", selector: (_, index) => index + 1, grow: 0.5 },
	...(type === "incoming"
		? [{ name: "From", selector: (row) => row.from_name, style: { textTransform: "capitalize" }, grow: 1.5 }]
		: [{ name: "To", selector: (row) => row.to_name, style: { textTransform: "capitalize" }, grow: 1.5 }]),
	{ name: "Status", cell: (row) => <span className={statusVariants({ status: row.status })}>{row.status}</span> },
	{ name: "Requested qty", selector: (row) => row.requested_qty },
	{ name: "Approved qty", selector: (row) => (row.status === "approved" ? row.approved_qty : "-") },
	{ name: "Date", selector: (row) => format(row.created_at, "PPP") },
	{
		name: "Actions",
		cell: (row) => (
			<CustomDropdown
				trigger={
					<Button variant="hover">
						<EllipsisIcon />
					</Button>
				}
				actions={[
					{
						label: (
							<CustomSheet title="Request Detail" trigger={<span>Request Detail</span>}>
								<RequestDetail requestUUID={row.uuid} />
							</CustomSheet>
						),
						asChild: true,
					},
				].appendToArray(
					[
						{
							label: (
								<CustomModal title="Approve Request" trigger={<span>Approve Request</span>}>
									<ApproveRequest requestUUID={row.uuid} />
								</CustomModal>
							),
							asChild: true,
						},
					],
					hasApproveInventoryPermission && type === "outgoing" && row.status === "pending"
				)}
			/>
		),
		grow: 0.5,
	},
];

const Table = ({ type = "incoming" }) => {
	const { user } = useAuth();
	const hasApproveInventoryPermission = useHasPermissions("approve_inventory_request");
	const [searchParams] = useSearchParams();
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;
	const entity = searchParams.get("entity") ?? user?.store_or_warehouse_uuid;

	const { data, isLoading } = useQuery({
		queryKey: ["requests", type, entity, page, limit],
		queryFn: () => (type === "incoming" ? getIncomingRequest({ entity, page, limit }) : getOutgoingRequest({ entity, page, limit })),
	});

	const requests = data?.data?.data ?? [];
	const totalRow = data?.data?.total;

	return (
		<CustomDataTable
			columns={columns(type, hasApproveInventoryPermission)}
			data={requests}
			progressPending={isLoading}
			paginationTotalRows={totalRow}
		/>
	);
};

const Comp = withPermissionWrapper(InventoryRequest, "view_store_requests");
export default Comp;
